/**
 * TimerManager — 遊戲計時器管理
 *
 * 管理遊戲倒數計時，提供 UI 更新回調。
 * 支援伺服器時間同步。
 */

export type TimerCallback = (remainingSeconds: number) => void;
export type TimerExpiredCallback = () => void;

export class TimerManager {
  private remainingSeconds: number = 0;
  private intervalId: ReturnType<typeof setInterval> | null = null;
  private onTickCallbacks: TimerCallback[] = [];
  private onExpiredCallbacks: TimerExpiredCallback[] = [];
  private expired: boolean = false;

  /**
   * 開始遊戲計時器
   * @param timeLimit 總時間（秒）
   * @param elapsedSeconds 已經過的秒數（用於重連同步）
   */
  startGameTimer(timeLimit: number, elapsedSeconds: number = 0): void {
    this.stopTimer();
    this.expired = false;
    this.remainingSeconds = Math.max(0, timeLimit - elapsedSeconds);

    this.intervalId = setInterval(() => {
      if (this.remainingSeconds > 0) {
        this.remainingSeconds--;
        this.notifyTick();
      }

      if (this.remainingSeconds <= 0 && !this.expired) {
        this.expired = true;
        this.notifyExpired();
      }
    }, 1000);

    // Emit initial tick
    this.notifyTick();
  }

  /**
   * 取得剩餘時間（秒）
   */
  getRemainingTime(): number {
    return this.remainingSeconds;
  }

  /**
   * 是否已過期
   */
  isExpired(): boolean {
    return this.expired;
  }

  /**
   * 與伺服器同步時間
   */
  syncTime(remainingSeconds: number): void {
    this.remainingSeconds = Math.max(0, remainingSeconds);
    if (this.remainingSeconds <= 0 && !this.expired) {
      this.expired = true;
      this.notifyExpired();
    }
    this.notifyTick();
  }

  /**
   * 註冊每秒更新回調
   */
  onTick(callback: TimerCallback): () => void {
    this.onTickCallbacks.push(callback);
    return () => {
      this.onTickCallbacks = this.onTickCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * 註冊計時器到期回調
   */
  onTimerExpired(callback: TimerExpiredCallback): () => void {
    this.onExpiredCallbacks.push(callback);
    return () => {
      this.onExpiredCallbacks = this.onExpiredCallbacks.filter(cb => cb !== callback);
    };
  }

  /**
   * 停止計時器
   */
  stopTimer(): void {
    if (this.intervalId !== null) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
  }

  /**
   * 清理所有資源
   */
  destroy(): void {
    this.stopTimer();
    this.onTickCallbacks = [];
    this.onExpiredCallbacks = [];
  }

  private notifyTick(): void {
    for (const cb of this.onTickCallbacks) {
      cb(this.remainingSeconds);
    }
  }

  private notifyExpired(): void {
    for (const cb of this.onExpiredCallbacks) {
      cb();
    }
  }
}

/**
 * 格式化秒數為 MM:SS 顯示
 */
export function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

/**
 * 取得計時器狀態等級
 */
export function getTimerLevel(remainingSeconds: number): 'normal' | 'warning' | 'critical' | 'expired' {
  if (remainingSeconds <= 0) return 'expired';
  if (remainingSeconds <= 60) return 'critical';
  if (remainingSeconds <= 300) return 'warning';
  return 'normal';
}

// Singleton instance for online game use
let globalTimerInstance: TimerManager | null = null;

export function getGlobalTimer(): TimerManager {
  if (!globalTimerInstance) {
    globalTimerInstance = new TimerManager();
  }
  return globalTimerInstance;
}

export function resetGlobalTimer(): void {
  if (globalTimerInstance) {
    globalTimerInstance.destroy();
    globalTimerInstance = null;
  }
}
